import type {
  BreakdownSubItem,
  BreakdownSubItemInput,
  EquipmentServiceRate,
  EquipmentType,
} from '@jobos/shared';
import {
  EQUIPMENT_BILL_DISCOUNT_TYPE,
  isCustomUnitTypeKey,
  buildBillDiscountSubItem,
  parseBillDiscountSubItem,
  buildFlatSubItem,
  buildItemDiscountSubItem,
  buildUnitSubItem,
  buildVisitSubItem,
  computeUnitTotal,
  isFlatType,
  isItemDiscountType,
  isUnitType,
  isVisitType,
  parseFlatSubItem,
  parseUnitSubItem,
  parseVisitSubItem,
} from '@jobos/shared';

/**
 * Rates typed in for a one-off piece of equipment. Present only on custom
 * units, which have no catalog row to read them from.
 */
export interface CustomUnitSpec {
  label: string;
  setupFee: number;
  dailyRate: number;
  minDays: number;
}

export interface EquipmentUnitUI {
  id: string;
  typeKey: string;
  assetTag: string;
  /** Set on custom units — carries the name and rates the catalog would hold. */
  custom?: CustomUnitSpec | null;
  location: string | null;
  startDate: string | null;
  endDate: string | null;
  /** Non-null → days-estimate mode (dates ignored). Null/undefined → date mode. */
  days?: number | null;
  /** Flat dollar discount for this unit, rendered as its own line downstream. */
  discount?: number | null;
}

export interface EquipmentVisitUI {
  id: string;
  rateKey: string;
  dates: string[];
  qty: number;
  /** Flat dollar discount for this visit, rendered as its own line downstream. */
  discount?: number | null;
}

export interface EquipmentFlatUI {
  id: string;
  description: string;
  amount: number;
  /** Flat dollar discount for this charge, rendered as its own line downstream. */
  discount?: number | null;
}

export interface EquipmentBillDiscountUI {
  id: string;
  /** Free text naming what is discounted. Blank falls back downstream. */
  label: string;
  amount: number;
}

export interface EquipmentUIState {
  units: EquipmentUnitUI[];
  visits: EquipmentVisitUI[];
  flats: EquipmentFlatUI[];
  /** Final-bill discounts, itemized — each renders as its own negative line. */
  billDiscounts: EquipmentBillDiscountUI[];
}

/**
 * What a unit needs to know about its type in order to bill and render —
 * read from the settings catalog for catalog units, from the unit itself for
 * custom ones. Every rate lookup goes through `resolveUnitType` so a unit is
 * never silently dropped for being absent from the catalog.
 */
export interface ResolvedUnitType {
  key: string;
  label: string;
  setupFee: number;
  dailyRate: number;
  minDays: number;
  sortOrder: number;
  /** True when the rates came from the unit rather than the catalog. */
  isCustom: boolean;
}

/** Custom groups sort after every catalog group, in the order they were added. */
const CUSTOM_TYPE_SORT_ORDER = Number.MAX_SAFE_INTEGER;

export function resolveUnitType(
  unit: EquipmentUnitUI,
  typeByKey: Map<string, EquipmentType>,
): ResolvedUnitType | null {
  if (isCustomUnitTypeKey(unit.typeKey)) {
    const c = unit.custom;
    if (!c) return null;
    return {
      key: unit.typeKey,
      label: c.label,
      setupFee: Number(c.setupFee),
      dailyRate: Number(c.dailyRate),
      minDays: Number(c.minDays),
      sortOrder: CUSTOM_TYPE_SORT_ORDER,
      isCustom: true,
    };
  }
  const t = typeByKey.get(unit.typeKey);
  if (!t) return null;
  return {
    key: t.key,
    label: t.label,
    setupFee: Number(t.setupFee),
    dailyRate: Number(t.dailyRate),
    minDays: t.minDays,
    sortOrder: t.sortOrder,
    isCustom: false,
  };
}

export interface UnitTypeGroup {
  type: ResolvedUnitType;
  units: EquipmentUnitUI[];
}

export interface EquipmentBillTotals {
  /** Each section total is net of that section's per-item discounts. */
  unitsTotal: number;
  visitsTotal: number;
  flatsTotal: number;
  subtotal: number;
  /** Sum of per-item discounts already deducted from the section totals. */
  itemDiscountTotal: number;
  /** Sum of the bill-level discounts, applied on top of the net subtotal. */
  discount: number;
  grandTotal: number;
}

export function readUIFromSubItems(
  subItems: BreakdownSubItem[],
  _types: EquipmentType[],
  _services: EquipmentServiceRate[],
): EquipmentUIState {
  const units: EquipmentUnitUI[] = [];
  const visits: EquipmentVisitUI[] = [];
  const flats: EquipmentFlatUI[] = [];
  const billDiscounts: EquipmentBillDiscountUI[] = [];

  const sorted = [...subItems].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  for (const s of sorted) {
    const type = s.type;
    const amount =
      (s as { amount?: number | null }).amount != null
        ? Number((s as { amount: number }).amount)
        : 0;
    const note = (s as { note?: string | null }).note ?? null;

    if (type === EQUIPMENT_BILL_DISCOUNT_TYPE) {
      const parsed = parseBillDiscountSubItem({ type, amount, note });
      if (parsed) {
        billDiscounts.push({ id: s.id, label: parsed.label, amount: parsed.discount });
      }
      continue;
    }

    // Per-item discount rows are derived from their item's note on every save,
    // so they carry no state of their own — skip them on read.
    if (isItemDiscountType(type)) continue;

    if (isFlatType(type)) {
      const parsed = parseFlatSubItem({ type, amount, note });
      if (!parsed) continue;
      flats.push({
        id: s.id,
        description: parsed.description,
        amount: parsed.amount,
        discount: parsed.discount,
      });
      continue;
    }

    if (isUnitType(type)) {
      const parsed = parseUnitSubItem({ type, amount, note });
      if (!parsed) continue;
      units.push({
        id: s.id,
        typeKey: parsed.typeKey,
        assetTag: parsed.assetTag,
        // A custom unit's name and rates live in its own note — the catalog
        // has nothing to look up.
        custom: isCustomUnitTypeKey(parsed.typeKey)
          ? {
              label: parsed.label ?? '',
              setupFee: Number(parsed.setupFee),
              dailyRate: Number(parsed.dailyRate),
              minDays: Number(parsed.minDays),
            }
          : null,
        location: parsed.location,
        startDate: parsed.startDate,
        endDate: parsed.endDate,
        days: parsed.days,
        discount: parsed.discount,
      });
      continue;
    }

    if (isVisitType(type)) {
      const parsed = parseVisitSubItem({ type, amount, note });
      if (!parsed) continue;
      visits.push({
        id: s.id,
        rateKey: parsed.rateKey,
        dates: parsed.dates,
        qty: parsed.qty,
        discount: parsed.discount,
      });
    }
  }

  return { units, visits, flats, billDiscounts };
}

/** Gross rental total for a unit, before any discount. */
function grossUnitTotal(u: EquipmentUnitUI, t: ResolvedUnitType): number {
  return computeUnitTotal({
    assetTag: u.assetTag,
    location: u.location,
    startDate: u.startDate,
    endDate: u.endDate,
    days: u.days ?? null,
    setupFee: t.setupFee,
    dailyRate: t.dailyRate,
    minDays: t.minDays,
  });
}

/** A discount, floored at 0 and capped at what its item bills. */
function clampDiscount(discount: number | null | undefined, gross: number): number {
  const raw = Number(discount ?? 0);
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  return Math.min(raw, gross);
}

export function clampUnitDiscount(u: EquipmentUnitUI, t: ResolvedUnitType): number {
  return clampDiscount(u.discount, grossUnitTotal(u, t));
}

export function clampVisitDiscount(v: EquipmentVisitUI, r: EquipmentServiceRate): number {
  return clampDiscount(v.discount, v.qty * Number(r.rate));
}

export function clampFlatDiscount(f: EquipmentFlatUI): number {
  return clampDiscount(f.discount, Number.isFinite(f.amount) ? f.amount : 0);
}

export function buildSubItemsFromUI(
  state: EquipmentUIState,
  types: EquipmentType[],
  services: EquipmentServiceRate[],
): BreakdownSubItemInput[] {
  const serviceByKey = new Map(services.map((s) => [s.key, s]));
  const out: BreakdownSubItemInput[] = [];
  let order = 0;

  // A discount follows its item so it renders directly underneath.
  const pushDiscount = (labelParts: Array<string | null | undefined>, discount: number) => {
    if (discount <= 0) return;
    const built = buildItemDiscountSubItem({ labelParts, discount, sortOrder: order++ });
    out.push({
      type: built.type,
      amount: built.amount,
      note: built.note,
      sortOrder: built.sortOrder,
    });
  };

  // Visits first (services group on top in the editor layout).
  for (const v of state.visits) {
    const rate = serviceByKey.get(v.rateKey);
    if (!rate) continue;
    const discount = clampVisitDiscount(v, rate);
    const built = buildVisitSubItem({
      rateKey: v.rateKey,
      label: rate.label,
      dates: v.dates,
      qty: v.qty,
      rate: Number(rate.rate),
      discount,
      sortOrder: order++,
    });
    out.push({
      type: built.type,
      amount: built.amount,
      note: built.note,
      sortOrder: built.sortOrder,
    });
    pushDiscount([rate.label], discount);
  }

  // Flats between visits and units.
  for (const f of state.flats) {
    const discount = clampFlatDiscount(f);
    const built = buildFlatSubItem({
      description: f.description,
      amount: f.amount,
      discount,
      sortOrder: order++,
    });
    out.push({ type: built.type, amount: built.amount, note: built.note, sortOrder: built.sortOrder });
    pushDiscount([f.description || 'Other charge'], discount);
  }

  // Units grouped by type sortOrder, units within a group sorted by asset tag.
  const grouped = groupUnitsByType(state.units, types);
  for (const group of grouped) {
    const t = group.type;
    for (const u of group.units) {
      const discount = clampUnitDiscount(u, t);
      const built = buildUnitSubItem({
        typeKey: u.typeKey,
        // Only custom units carry a label; catalog units keep reading theirs
        // from the catalog, so renaming a type there still reaches old rows.
        label: t.isCustom ? t.label : null,
        assetTag: u.assetTag,
        location: u.location,
        startDate: u.startDate,
        endDate: u.endDate,
        days: u.days ?? null,
        discount,
        setupFee: t.setupFee,
        dailyRate: t.dailyRate,
        minDays: t.minDays,
        sortOrder: order++,
      });
      out.push({
        type: built.type,
        amount: built.amount,
        note: built.note,
        sortOrder: built.sortOrder,
      });

      pushDiscount([t.label, u.assetTag], discount);
    }
  }

  for (const d of state.billDiscounts) {
    if (!(d.amount > 0)) continue;
    const built = buildBillDiscountSubItem({
      label: d.label,
      discount: d.amount,
      sortOrder: order++,
    });
    out.push({ type: built.type, amount: built.amount, note: built.note, sortOrder: built.sortOrder });
  }

  return out;
}

export function groupUnitsByType(
  units: EquipmentUnitUI[],
  types: EquipmentType[],
): UnitTypeGroup[] {
  const typeByKey = new Map(types.map((t) => [t.key, t]));
  // Custom keys are unique per unit, so each custom unit lands in its own
  // group — two air scrubbers added separately stay two rows, not one pair.
  const groups = new Map<string, { type: ResolvedUnitType; units: EquipmentUnitUI[] }>();
  for (const u of units) {
    const t = resolveUnitType(u, typeByKey);
    if (!t) continue;
    const existing = groups.get(u.typeKey);
    if (existing) existing.units.push(u);
    else groups.set(u.typeKey, { type: t, units: [u] });
  }

  const result: UnitTypeGroup[] = [];
  for (const group of groups.values()) {
    group.units.sort((a, b) =>
      a.assetTag.localeCompare(b.assetTag, undefined, { numeric: true, sensitivity: 'base' }),
    );
    result.push(group);
  }
  // Stable sort keeps custom groups (all sharing the max sort order) in the
  // order they were added.
  result.sort((a, b) => a.type.sortOrder - b.type.sortOrder);
  return result;
}

const SHORT_DATE_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

export function formatShortDate(iso: string): string {
  // Treat YYYY-MM-DD as a calendar date in the local zone (no UTC drift).
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return SHORT_DATE_FMT.format(new Date(y, m - 1, d));
}

export function formatDateList(dates: string[]): string {
  if (dates.length === 0) return '—';
  const sorted = [...dates].sort();
  const head = sorted.slice(0, 3).map(formatShortDate).join(', ');
  return sorted.length > 3 ? `${head} +${sorted.length - 3}` : head;
}

export function computeBillTotals(
  state: EquipmentUIState,
  types: EquipmentType[],
  services: EquipmentServiceRate[],
): EquipmentBillTotals {
  const typeByKey = new Map(types.map((t) => [t.key, t]));
  const serviceByKey = new Map(services.map((s) => [s.key, s]));

  let itemDiscountTotal = 0;

  let unitsTotal = 0;
  for (const u of state.units) {
    const t = resolveUnitType(u, typeByKey);
    if (!t) continue;
    const discount = clampUnitDiscount(u, t);
    itemDiscountTotal += discount;
    unitsTotal += grossUnitTotal(u, t) - discount;
  }

  let visitsTotal = 0;
  for (const v of state.visits) {
    const rate = serviceByKey.get(v.rateKey);
    if (!rate) continue;
    const discount = clampVisitDiscount(v, rate);
    itemDiscountTotal += discount;
    visitsTotal += v.qty * Number(rate.rate) - discount;
  }

  let flatsTotal = 0;
  for (const f of state.flats) {
    const gross = Number.isFinite(f.amount) ? f.amount : 0;
    const discount = clampFlatDiscount(f);
    itemDiscountTotal += discount;
    flatsTotal += gross - discount;
  }

  const subtotal = unitsTotal + visitsTotal + flatsTotal;
  const discount = state.billDiscounts.reduce(
    (sum, d) => sum + Math.max(0, d.amount || 0),
    0,
  );
  const grandTotal = Math.max(0, subtotal - discount);
  return {
    unitsTotal,
    visitsTotal,
    flatsTotal,
    subtotal,
    itemDiscountTotal,
    discount,
    grandTotal,
  };
}
